import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db, auth } from "../firebase";

export default function EditTrip({ trip, onBack, onDeleted, onUpdated }) {
  const [activeSection, setActiveSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDelete2, setConfirmDelete2] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [form, setForm] = useState({
    name: trip.name || "",
    destination: trip.destination || "",
    country: trip.country || "",
    startDate: trip.startDate || "",
    endDate: trip.endDate || "",
    notes: trip.notes || "",
    objective: trip.objective || "",
  });
  const [inviteToken, setInviteToken] = useState(trip.inviteToken || null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  // Dashboard's trips state may not carry inviteToken if it was generated after load.
  // Fetch fresh from Firestore the first time the invite panel opens so Regenerate appears.
  useEffect(() => {
    if (activeSection !== "invite" || inviteToken) return;
    getDoc(doc(db, "trips", trip.id)).then(snap => {
      if (snap.exists() && snap.data().inviteToken) {
        setInviteToken(snap.data().inviteToken);
      }
    }).catch(() => {});
  }, [activeSection, trip.id]);

  const getDays = () => {
    if (!form.startDate || !form.endDate) return 0;
    const diff = new Date(form.endDate) - new Date(form.startDate);
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedData = {
        ...form,
        days: getDays(),
      };
      await updateDoc(doc(db, "trips", trip.id), updatedData);
      onUpdated({ ...trip, ...updatedData });
    } catch (err) {
      console.error(err);
      setSaveError("Failed to save. Please try again.");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDoc(doc(db, "trips", trip.id));
      onDeleted();
    } catch (err) {
      console.error("Delete trip failed:", err);
      setDeleteError("Failed to delete trip. Please try again.");
      setConfirmDelete(false);
      setConfirmDelete2(false);
    }
    setDeleting(false);
  };

  const handleCopyLink = async () => {
    try {
      const isNewToken = !inviteToken;
      const token = inviteToken || crypto.randomUUID();
      const link = `${window.location.origin}/join/${trip.id}?token=${token}`;
      await navigator.clipboard.writeText(link);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
      if (isNewToken) {
        await updateDoc(doc(db, "trips", trip.id), { inviteToken: token });
        setInviteToken(token);
      }
    } catch (err) {
      console.error("[copyLink]", err);
      setInviteError("Failed to copy link. Please try again.");
    }
  };

  const handleRegenLink = async () => {
    try {
      const newToken = crypto.randomUUID();
      await updateDoc(doc(db, "trips", trip.id), { inviteToken: newToken });
      setInviteToken(newToken);
      const link = `${window.location.origin}/join/${trip.id}?token=${newToken}`;
      navigator.clipboard.writeText(link);
      setConfirmRegen(false);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch (err) {
      console.error("[regenLink]", err);
      setInviteError("Failed to regenerate link. Please try again.");
    }
  };

  const sections = [
    { id: "details", label: "Trip details", icon: "✏️", desc: "Name, destination, country" },
    { id: "dates", label: "Travel dates", icon: "📅", desc: "Change your departure & return" },
    { id: "objective", label: "Trip objective", icon: "🎯", desc: "What do you want from this trip?" },
    { id: "notes", label: "Notes for AI", icon: "🤖", desc: "Hints to improve your itinerary" },
    { id: "invite", label: "Invite travelers", icon: "👥", desc: "Share your invite link" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .et-root {
          min-height: 100vh;
          background: #FAF6F0;
          font-family: 'DM Sans', sans-serif;
          padding-bottom: 40px;
        }

        .et-nav {
          background: white;
          border-bottom: 0.5px solid rgba(26,23,20,0.08);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .et-back {
          font-size: 14px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          cursor: pointer;
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }

        .et-back:hover { color: #F5A623; }

        .et-nav-title {
          font-family: 'Playfair Display', serif;
          font-size: 16px;
          font-weight: 600;
          color: #1a1714;
        }

        .et-save {
          font-size: 13px;
          font-weight: 500;
          color: white;
          background: #F5A623;
          border: none;
          border-radius: 100px;
          padding: 7px 16px;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: background 0.2s;
        }

        .et-save:hover { background: #1E2D4A; }
        .et-save:disabled { opacity: 0.5; cursor: not-allowed; }

        .et-hero {
          background: #1E2D4A;
          padding: 24px;
        }

        .et-trip-name {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: white;
          margin-bottom: 4px;
        }

        .et-trip-meta {
          font-size: 13px;
          color: rgba(255,255,255,0.5);
          font-weight: 300;
        }

        .et-content { padding: 20px; }

        .et-section-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        }

        .et-section-btn {
          background: white;
          border: 0.5px solid rgba(26,23,20,0.08);
          border-radius: 14px;
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.15s;
          width: 100%;
          font-family: 'DM Sans', sans-serif;
          text-align: left;
        }

        .et-section-btn:hover { border-color: #F5A623; transform: translateY(-1px); }
        .et-section-btn.active { border-color: #F5A623; background: #e3eef6; }

        .et-section-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: #FAF6F0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          flex-shrink: 0;
        }

        .et-section-btn.active .et-section-icon { background: white; }

        .et-section-info { flex: 1; }

        .et-section-label {
          font-size: 14px;
          font-weight: 500;
          color: #1a1714;
          margin-bottom: 2px;
        }

        .et-section-desc {
          font-size: 12px;
          color: rgba(26,23,20,0.45);
          font-weight: 300;
        }

        .et-chevron {
          font-size: 16px;
          color: rgba(26,23,20,0.3);
        }

        .et-panel {
          background: white;
          border-radius: 16px;
          border: 0.5px solid rgba(26,23,20,0.08);
          padding: 20px;
          margin-bottom: 12px;
          animation: fadeUp 0.3s ease forwards;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .et-panel-title {
          font-size: 13px;
          font-weight: 500;
          color: rgba(26,23,20,0.5);
          text-transform: uppercase;
          letter-spacing: 0.06em;
          margin-bottom: 14px;
        }

        .et-field { margin-bottom: 12px; }

        .et-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          margin-bottom: 5px;
        }

        .et-input {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 10px;
          background: #fafaf8;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #1a1714;
          outline: none;
          transition: border-color 0.2s;
        }

        .et-input:focus {
          border-color: #F5A623;
          background: white;
        }

        .et-input::placeholder { color: rgba(26,23,20,0.3); }

        .et-textarea {
          width: 100%;
          padding: 12px 14px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 10px;
          background: #fafaf8;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #1a1714;
          outline: none;
          resize: none;
          height: 100px;
          transition: border-color 0.2s;
        }

        .et-textarea:focus {
          border-color: #F5A623;
          background: white;
        }

        .et-textarea::placeholder { color: rgba(26,23,20,0.3); }

        .et-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

        .et-days-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #e3eef6;
          border-radius: 100px;
          padding: 5px 12px;
          font-size: 12px;
          font-weight: 500;
          color: #F5A623;
          margin-top: 8px;
        }

        .et-invite-row { display: flex; gap: 8px; margin-bottom: 10px; }

        .et-invite-btn {
          padding: 12px 16px;
          background: #F5A623;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          white-space: nowrap;
        }

        .et-member-row {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #FAF6F0;
          border-radius: 8px;
          padding: 8px 10px;
          margin-bottom: 6px;
        }

        .et-member-av {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          background: #1E2D4A;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 500;
          color: white;
          flex-shrink: 0;
        }

        .et-member-email { flex: 1; font-size: 12px; color: #1a1714; }

        .et-member-remove {
          background: none;
          border: none;
          font-size: 16px;
          color: rgba(26,23,20,0.3);
          cursor: pointer;
          padding: 0;
        }

        .et-member-remove:hover { color: #c9622f; }

        .et-objective-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .et-obj-btn {
          padding: 12px 10px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 10px;
          background: #fafaf8;
          font-size: 12px;
          font-weight: 500;
          color: rgba(26,23,20,0.65);
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          text-align: center;
          transition: all 0.15s;
        }

        .et-obj-btn:hover { border-color: #7BA7C4; }
        .et-obj-btn.selected { background: #F5A623; color: white; border-color: #F5A623; }

        .et-danger-zone {
          background: white;
          border: 0.5px solid rgba(201,98,47,0.2);
          border-radius: 16px;
          padding: 18px 20px;
          margin-top: 8px;
        }

        .et-danger-title {
          font-size: 13px;
          font-weight: 500;
          color: #c9622f;
          margin-bottom: 12px;
        }

        .et-delete-btn {
          width: 100%;
          padding: 12px;
          border: 1.5px solid #c9622f;
          border-radius: 10px;
          background: white;
          font-size: 13px;
          font-weight: 500;
          color: #c9622f;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.2s;
        }

        .et-delete-btn:hover { background: #c9622f; color: white; }

        .et-confirm-delete {
          background: #fef0ed;
          border-radius: 10px;
          padding: 14px;
          margin-top: 10px;
        }

        .et-confirm-text {
          font-size: 13px;
          color: #712B13;
          margin-bottom: 12px;
          line-height: 1.5;
        }

        .et-confirm-btns { display: flex; gap: 8px; }

        .et-confirm-yes {
          flex: 1;
          padding: 10px;
          background: #c9622f;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
        }

        .et-confirm-no {
          flex: 1;
          padding: 10px;
          background: white;
          color: rgba(26,23,20,0.6);
          border: 1px solid rgba(26,23,20,0.15);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
        }
      `}</style>

      <div className="et-root">
        <div className="et-nav">
          <button className="et-back" onClick={onBack}>← Back</button>
          <span className="et-nav-title">Edit trip</span>
          <button className="et-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

        <div className="et-hero">
          <div className="et-trip-name">{trip.name}</div>
          <div className="et-trip-meta">📍 {trip.destination} · {trip.days} days</div>
        </div>

        <div className="et-content">
          {saveError && (
            <p style={{fontSize:13,color:'#c9622f',marginBottom:10}}>{saveError}</p>
          )}
          <div className="et-section-list">
            {sections.map((s) => (
              <button
                key={s.id}
                className={`et-section-btn ${activeSection === s.id ? "active" : ""}`}
                onClick={() => setActiveSection(activeSection === s.id ? null : s.id)}
              >
                <div className="et-section-icon">{s.icon}</div>
                <div className="et-section-info">
                  <div className="et-section-label">{s.label}</div>
                  <div className="et-section-desc">{s.desc}</div>
                </div>
                <span className="et-chevron">{activeSection === s.id ? "↑" : "↓"}</span>
              </button>
            ))}
          </div>

          {/* DETAILS PANEL */}
          {activeSection === "details" && (
            <div className="et-panel">
              <div className="et-panel-title">Trip details</div>
              <div className="et-field">
                <label className="et-label">Trip name</label>
                <input className="et-input" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})} />
              </div>
              <div className="et-field">
                <label className="et-label">Destination city</label>
                <input className="et-input" value={form.destination}
                  onChange={e => setForm({...form, destination: e.target.value})} />
              </div>
              <div className="et-field">
                <label className="et-label">Country</label>
                <input className="et-input" value={form.country}
                  onChange={e => setForm({...form, country: e.target.value})} />
              </div>
            </div>
          )}

          {/* DATES PANEL */}
          {activeSection === "dates" && (
            <div className="et-panel">
              <div className="et-panel-title">Travel dates</div>
              <div className="et-dates">
                <div className="et-field" style={{marginBottom:0}}>
                  <label className="et-label">Departure</label>
                  <input className="et-input" type="date" value={form.startDate}
                    onChange={e => setForm({...form, startDate: e.target.value})} />
                </div>
                <div className="et-field" style={{marginBottom:0}}>
                  <label className="et-label">Return</label>
                  <input className="et-input" type="date" value={form.endDate}
                    onChange={e => setForm({...form, endDate: e.target.value})} />
                </div>
              </div>
              {getDays() > 0 && (
                <div className="et-days-badge">
                  📅 {getDays()} days
                </div>
              )}
            </div>
          )}

          {/* OBJECTIVE PANEL */}
          {activeSection === "objective" && (
            <div className="et-panel">
              <div className="et-panel-title">Trip objective</div>
              <div className="et-objective-options">
                {["Relaxation & beach", "Culture & history", "Adventure & hiking", "Food & nightlife", "Romantic getaway", "Family friendly", "Party & social", "Off the beaten path"].map(obj => (
                  <button key={obj}
                    className={`et-obj-btn ${form.objective === obj ? "selected" : ""}`}
                    onClick={() => setForm({...form, objective: obj})}>
                    {obj}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* NOTES PANEL */}
          {activeSection === "notes" && (
            <div className="et-panel">
              <div className="et-panel-title">Notes for AI planner</div>
              <textarea className="et-textarea"
                placeholder="e.g. We want mostly beaches, avoid tourist traps, one of us has a bad knee so no intense hikes..."
                value={form.notes}
                onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
          )}

          {/* INVITE PANEL */}
          {activeSection === "invite" && (
            <div className="et-panel">
              <div className="et-panel-title">Invite travelers</div>
              <p style={{fontSize:13,color:'rgba(26,23,20,0.5)',marginBottom:14,fontWeight:300,lineHeight:1.5}}>
                Share this link with anyone you want to join the trip. A Solis account is required to join.
              </p>

              {inviteToken ? (
                <div style={{
                  background:'#FAF6F0',borderRadius:10,padding:'10px 12px',
                  fontSize:11,color:'rgba(26,23,20,0.55)',wordBreak:'break-all',
                  marginBottom:12,fontFamily:'monospace',lineHeight:1.7
                }}>
                  {window.location.origin}/join/{trip.id}?token={inviteToken}
                </div>
              ) : (
                <p style={{fontSize:12,color:'rgba(26,23,20,0.35)',marginBottom:12,fontStyle:'italic'}}>
                  Click "Copy link" to generate your invite link.
                </p>
              )}

              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <button className="et-invite-btn" onClick={handleCopyLink} style={{flex:1}}>
                  {inviteCopied ? "✓ Copied!" : "Copy link"}
                </button>
                {inviteToken && !confirmRegen && (
                  <button onClick={() => setConfirmRegen(true)} style={{
                    background:'none',border:'none',fontSize:12,
                    color:'rgba(26,23,20,0.45)',cursor:'pointer',
                    fontFamily:'DM Sans, sans-serif',textDecoration:'underline',
                    padding:'0 4px',flexShrink:0
                  }}>
                    Regenerate
                  </button>
                )}
              </div>

              {confirmRegen && (
                <div style={{background:'#fef0ed',borderRadius:10,padding:'12px 14px',marginTop:12}}>
                  <p style={{fontSize:13,color:'#c9622f',marginBottom:10,lineHeight:1.5}}>
                    This invalidates the current link. Anyone with the old link won't be able to join. Continue?
                  </p>
                  <div style={{display:'flex',gap:8}}>
                    <button className="et-invite-btn" style={{background:'#c9622f',fontSize:12,padding:'8px 14px'}}
                      onClick={handleRegenLink}>
                      Yes, regenerate
                    </button>
                    <button onClick={() => setConfirmRegen(false)} style={{
                      background:'none',border:'none',fontSize:12,
                      color:'rgba(26,23,20,0.5)',cursor:'pointer',
                      fontFamily:'DM Sans, sans-serif'
                    }}>Cancel</button>
                  </div>
                </div>
              )}
              {inviteError && (
                <p style={{fontSize:13,color:'#c9622f',marginTop:10}}>{inviteError}</p>
              )}
            </div>
          )}

          {/* DANGER ZONE — only the trip creator can delete */}
          {auth.currentUser?.uid === trip.createdBy && (
            <div className="et-danger-zone">
              <div className="et-danger-title">Danger zone</div>
              {deleteError && (
                <p style={{fontSize:13,color:'#c9622f',marginBottom:10}}>{deleteError}</p>
              )}
              {!confirmDelete && (
                <button className="et-delete-btn" onClick={() => { setDeleteError(null); setConfirmDelete(true); }}>
                  🗑 Delete this trip
                </button>
              )}
              {confirmDelete && !confirmDelete2 && (
                <div className="et-confirm-delete">
                  <p className="et-confirm-text">
                    Are you sure you want to delete <strong>{trip.name}</strong>? This will permanently remove all itinerary, expenses, bookings, and journal entries.
                  </p>
                  <div className="et-confirm-btns">
                    <button className="et-confirm-yes" onClick={() => setConfirmDelete2(true)}>
                      Yes, continue
                    </button>
                    <button className="et-confirm-no" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {confirmDelete2 && (
                <div className="et-confirm-delete" style={{background:'#fde8e1'}}>
                  <p className="et-confirm-text">
                    This cannot be undone. <strong>{trip.name}</strong> will be gone forever.
                  </p>
                  <div className="et-confirm-btns">
                    <button className="et-confirm-yes" onClick={handleDelete} disabled={deleting}>
                      {deleting ? "Deleting..." : "Delete forever"}
                    </button>
                    <button className="et-confirm-no" onClick={() => { setConfirmDelete(false); setConfirmDelete2(false); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}